import React, { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { useOrganization } from "../context/OrganizationContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { 
  Building2, Upload, Save, Trash2, Loader2, CheckCircle,
  Crown, Sparkles, ArrowUpRight, Check, Users, HardDrive, Search, FileText,
  Shield, AlertTriangle, Clock, Zap
} from "lucide-react";
import { toast } from "sonner";
import { PLANS_DATA, PLAN_HIERARCHY, getPrice, getPriceLabel } from "../data/plans";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Plan display info
const PLAN_INFO = {
  free: { name: "Free", color: "slate", icon: null, price: "$0" },
  basic: { name: "Basic", color: "blue", icon: null, price: "$19/user" },
  standard: { name: "Standard", color: "purple", icon: Sparkles, price: "$39/user" },
  pro: { name: "Pro", color: "amber", icon: Crown, price: "$59/user" },
  enterprise: { name: "Enterprise", color: "emerald", icon: Crown, price: "Custom" },
};

const PLAN_COLORS = {
  free: "bg-slate-500/10 text-slate-300 border-slate-500/30",
  basic: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  standard: "bg-purple-500/10 text-purple-300 border-purple-500/30",
  pro: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  enterprise: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
};

function UpgradePlanModal({ isOpen, onClose, currentPlan }) {
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [couponCode, setCouponCode] = useState("");
  const [processing, setProcessing] = useState(false);
  
  if (!isOpen) return null;

  const upgradePlans = ["basic", "standard", "pro"]
    .map((id) => PLANS_DATA[id])
    .filter(Boolean);

  const currentIndex = PLAN_HIERARCHY.indexOf(currentPlan);
  
  const handleUpgrade = async (plan) => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/billing/checkout`,
        {
          plan: plan.id,
          billing_period: billingPeriod,
          quantity: plan.minSeats,
          coupon_code: couponCode || null,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar checkout");
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
            <Crown className="w-7 h-7 text-amber-400" />
          </div>
          <h2 className="text-2xl font-bold text-white">Mejora tu plan</h2>
          <p className="text-slate-400 mt-2">Desbloquea más funcionalidades para tu equipo</p>
        </div>

        {/* Billing Period Toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setBillingPeriod("monthly")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingPeriod === "monthly"
                  ? "bg-teal-600 text-white"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              Mensual
            </button>
            <button
              onClick={() => setBillingPeriod("annual")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingPeriod === "annual"
                  ? "bg-teal-600 text-white"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              Anual
              <span className="ml-2 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
                Ahorra 18%
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {upgradePlans.map((plan) => {
            const planIndex = PLAN_HIERARCHY.indexOf(plan.id);
            const isCurrentOrLower = planIndex <= currentIndex;
            const price = getPrice(plan.id, billingPeriod);
            const priceLabel = getPriceLabel(billingPeriod);
            
            return (
              <div
                key={plan.id}
                className={`relative rounded-xl p-5 border ${
                  plan.highlight 
                    ? "border-teal-500/50 bg-teal-500/5" 
                    : "border-slate-700 bg-slate-800/50"
                } ${isCurrentOrLower ? "opacity-50" : ""}`}
              >
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                      <Sparkles size={12} /> Recomendado
                    </span>
                  </div>
                )}
                
                <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                <p className="text-xs text-slate-400 mb-2">{plan.description}</p>
                <div className="mt-2 mb-1">
                  <span className="text-2xl font-bold text-white">${price} USD</span>
                  <span className="text-slate-400 text-sm ml-1">{priceLabel}</span>
                </div>
                <p className="text-xs text-slate-500 mb-4">Mínimo {plan.minSeats} {plan.minSeats === 1 ? 'usuario' : 'usuarios'}</p>
                
                <ul className="space-y-2 mb-4">
                  {plan.shortFeatures.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-teal-400" />
                      {f}
                    </li>
                  ))}
                </ul>
                
                <Button
                  disabled={isCurrentOrLower || processing}
                  onClick={() => handleUpgrade(plan)}
                  className={`w-full ${
                    isCurrentOrLower 
                      ? "bg-slate-700 text-slate-400 cursor-not-allowed" 
                      : "bg-teal-600 hover:bg-teal-500 text-white"
                  }`}
                >
                  {processing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando...</>
                  ) : isCurrentOrLower ? (
                    "Plan actual o inferior"
                  ) : (
                    "Seleccionar Plan"
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Coupon Code */}
        <div className="mb-6">
          <Label className="text-slate-400 text-sm mb-2">¿Tienes un código de descuento?</Label>
          <Input
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            placeholder="Ingresa código"
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>

        <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-white font-medium">¿Necesitas Enterprise?</h4>
              <p className="text-slate-400 text-sm">Contacta a ventas para una solución personalizada</p>
            </div>
            <Button variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
              Contactar
            </Button>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={onClose} variant="outline" className="border-slate-600 text-slate-300" disabled={processing}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}

// Seats Management Modal
function SeatsManagementModal({ isOpen, onClose, seatsInfo, onUpdate }) {
  const [newQuantity, setNewQuantity] = useState(seatsInfo?.total_seats || 0);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (seatsInfo) {
      setNewQuantity(seatsInfo.total_seats);
    }
  }, [seatsInfo]);

  if (!isOpen || !seatsInfo) return null;

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await axios.put(
        `${API}/billing/seats`,
        { quantity: newQuantity },
        { withCredentials: true }
      );
      toast.success("Licencias actualizadas correctamente");
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar licencias");
    } finally {
      setUpdating(false);
    }
  };

  const canDecrease = newQuantity > Math.max(seatsInfo.used_seats, seatsInfo.min_seats);
  const minAllowed = Math.max(seatsInfo.used_seats, seatsInfo.min_seats);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-teal-500/10 flex items-center justify-center mx-auto mb-4">
            <Users className="w-7 h-7 text-teal-400" />
          </div>
          <h2 className="text-2xl font-bold text-white">Ajustar Licencias</h2>
          <p className="text-slate-400 mt-2">Modifica el número de licencias de usuario</p>
        </div>

        <div className="space-y-4 mb-6">
          <div className="bg-slate-800/50 rounded-xl p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-400">Licencias actuales</p>
                <p className="text-white font-bold text-xl">{seatsInfo.total_seats}</p>
              </div>
              <div>
                <p className="text-slate-400">En uso</p>
                <p className="text-white font-bold text-xl">{seatsInfo.used_seats}</p>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-slate-400 text-sm mb-2">Nuevas Licencias</Label>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setNewQuantity(Math.max(minAllowed, newQuantity - 1))}
                disabled={!canDecrease || updating}
                variant="outline"
                className="border-slate-600 text-slate-300"
                size="icon"
              >
                -
              </Button>
              <Input
                type="number"
                value={newQuantity}
                onChange={(e) => setNewQuantity(Math.max(minAllowed, parseInt(e.target.value) || minAllowed))}
                min={minAllowed}
                className="bg-slate-800 border-slate-700 text-white text-center text-xl font-bold"
                disabled={updating}
              />
              <Button
                onClick={() => setNewQuantity(newQuantity + 1)}
                disabled={updating}
                variant="outline"
                className="border-slate-600 text-slate-300"
                size="icon"
              >
                +
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Mínimo: {minAllowed} ({seatsInfo.used_seats} en uso, {seatsInfo.min_seats} requeridos por plan)
            </p>
          </div>

          {newQuantity !== seatsInfo.total_seats && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              <p className="text-amber-400 text-sm">
                {newQuantity > seatsInfo.total_seats ? (
                  <>
                    Se te cobrará la diferencia de forma prorrateada inmediatamente.
                    Nuevas licencias: <strong>{newQuantity - seatsInfo.total_seats}</strong>
                  </>
                ) : (
                  <>
                    Se te acreditará la diferencia en tu próxima factura.
                    Reducción: <strong>{seatsInfo.total_seats - newQuantity}</strong>
                  </>
                )}
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1 border-slate-600 text-slate-300"
            disabled={updating}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={newQuantity === seatsInfo.total_seats || updating}
            className="flex-1 bg-teal-600 hover:bg-teal-500 text-white"
          >
            {updating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Actualizando...
              </>
            ) : (
              "Actualizar"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function OrganizationSettings() {
  const { user } = useAuth();
  const { organization, plan, planName, refetch: refetchOrg } = useOrganization();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [seatsModalOpen, setSeatsModalOpen] = useState(false);
  const [seatsInfo, setSeatsInfo] = useState(null);
  const [settings, setSettings] = useState({
    company_name: "",
    legal_representative_name: "",
    legal_representative_title: "",
    tax_id: "",
    address: "",
    phone: "",
    email: "",
    website: "",
    logo_url: null,
    // Campos adicionales de Organization
    legal_name: "",
    business_line: "",
    activity_type: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [businessLines, setBusinessLines] = useState([]);
  const [amlStatus, setAmlStatus] = useState(null);
  const [uploadingAml, setUploadingAml] = useState(false);

  useEffect(() => {
    // Always refetch the org on mount so the plan badge reflects what
    // the Superadmin assigned, even if the user left this tab open
    // since their initial login (the context only fetches once per
    // session by default).
    refetchOrg?.();
    fetchSettings();
    if (plan !== "free" && user?.role === "owner") {
      fetchSeatsInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, user]);

  const fetchSettings = async () => {
    try {
      const [settingsRes, blRes, orgRes] = await Promise.all([
        axios.get(`${API}/organizations/settings`, { withCredentials: true }),
        axios.get(`${API}/organizations/business-lines`, { withCredentials: true }),
        axios.get(`${API}/organizations`, { withCredentials: true }),
      ]);
      
      setSettings(settingsRes.data);
      setBusinessLines(blRes.data.items || []);
      
      // Get AML status from organization
      const orgItems = orgRes.data.items;
      if (orgItems.length > 0) {
        setAmlStatus({
          status: orgItems[0].aml_registration_status || "pending",
          filename: orgItems[0].aml_registration_filename,
        });
        
        // Merge additional fields from organization
        setSettings(prev => ({
          ...prev,
          legal_name: orgItems[0].legal_name || "",
          business_line: orgItems[0].business_line || "",
          activity_type: orgItems[0].activity_type || "",
        }));
      }
    } catch (err) {
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Validate business line
      if (!settings.business_line) {
        toast.error("Giro de negocio es obligatorio");
        setSaving(false);
        return;
      }
      
      // Update organization settings
      await axios.put(`${API}/organizations/settings`, {
        company_name: settings.company_name || "",
        legal_representative_name: settings.legal_representative_name || "",
        legal_representative_title: settings.legal_representative_title || "",
        tax_id: settings.tax_id || "",
        address: settings.address || "",
        phone: settings.phone || "",
        email: settings.email || "",
        website: settings.website || "",
      }, { withCredentials: true });
      
      // Update organization general info
      if (organization) {
        await axios.put(`${API}/organizations/${organization.organization_id}`, {
          name: settings.company_name || "",
          legal_name: settings.legal_name || "",
          business_line: settings.business_line,
          activity_type: settings.activity_type || "",
          rfc: settings.tax_id || "",
          address: settings.address || "",
          phone: settings.phone || "",
          email: settings.email || "",
        }, { withCredentials: true });
      }
      
      toast.success("Configuración guardada exitosamente");
    } catch (err) {
      // Handle Pydantic validation errors
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const messages = detail.map(e => e.msg || e.message || "Error de validación").join(", ");
        toast.error(messages);
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else if (detail && typeof detail === "object") {
        toast.error(detail.msg || detail.message || "Error al guardar configuración");
      } else {
        toast.error("Error al guardar configuración");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"];
    if (!validTypes.includes(file.type)) {
      toast.error("Formato no permitido. Use PNG, JPG o SVG");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo excede el límite de 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(`${API}/organizations/upload-logo`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSettings({ ...settings, logo_url: res.data.logo_url });
      toast.success("Logo subido exitosamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir logo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm("¿Eliminar el logo de la organización?")) return;

    try {
      await axios.delete(`${API}/organizations/logo`, { withCredentials: true });
      setSettings({ ...settings, logo_url: null });
      toast.success("Logo eliminado");
    } catch (err) {
      toast.error("Error al eliminar logo");
    }
  };

  const handleAmlUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !organization) return;
    
    setUploadingAml(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/organizations/${organization.organization_id}/aml-registration`,
        fd,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      setAmlStatus({ status: "uploaded", filename: res.data.filename });
      toast.success("Documento de registro AML subido");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir documento");
    } finally {
      setUploadingAml(false);
      e.target.value = "";
    }
  };



  const fetchSeatsInfo = async () => {
    try {
      const res = await axios.get(`${API}/billing/seats`, { withCredentials: true });
      setSeatsInfo(res.data);
    } catch (err) {
      console.error("Error loading seats info:", err);
    }
  };

  const handleCustomerPortal = async () => {
    try {
      const res = await axios.get(`${API}/billing/portal`, { withCredentials: true });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al abrir portal de facturación");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 size={32} className="animate-spin text-teal-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <UpgradePlanModal 
        isOpen={upgradeModalOpen} 
        onClose={() => setUpgradeModalOpen(false)} 
        currentPlan={plan}
      />
      
      <SeatsManagementModal
        isOpen={seatsModalOpen}
        onClose={() => setSeatsModalOpen(false)}
        seatsInfo={seatsInfo}
        onUpdate={() => {
          fetchSeatsInfo();
          setSeatsModalOpen(false);
        }}
      />
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Building2 size={28} className="text-teal-400" />
            <h1 className="text-2xl font-bold text-white">Configuración de Organización</h1>
          </div>
          <p className="text-slate-400 text-sm">
            Configure los datos de su empresa para personalizar los documentos KYC
          </p>
        </div>

        <div className="space-y-6">
          {/* Plan y Suscripción */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <h3 className="text-base font-semibold text-white mb-4">Plan y Suscripción</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`px-4 py-2 rounded-lg border ${PLAN_COLORS[plan]}`}>
                  <div className="flex items-center gap-2">
                    {PLAN_INFO[plan]?.icon && React.createElement(PLAN_INFO[plan].icon, { className: "w-4 h-4" })}
                    <span className="font-semibold">{planName}</span>
                  </div>
                </div>
                <div className="text-sm">
                  <p className="text-slate-400">Plan actual</p>
                  <p className="text-white font-medium">{organization?.name || "Cargando..."}</p>
                </div>
              </div>
              <Button
                onClick={() => setUpgradeModalOpen(true)}
                className="bg-teal-600 hover:bg-teal-500 text-white"
              >
                <ArrowUpRight size={16} className="mr-2" />
                Actualizar Plan
              </Button>
            </div>
            
            {/* Usage Stats Preview */}
            <div className="mt-4 pt-4 border-t border-slate-800">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span className="text-xs text-slate-400">Usuarios</span>
                  </div>
                  <p className="text-white font-semibold">
                    {organization?.limits?.max_users || 0}
                  </p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span className="text-xs text-slate-400">Clientes</span>
                  </div>
                  <p className="text-white font-semibold">
                    {organization?.limits?.max_customers || 0}
                  </p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <HardDrive className="w-4 h-4 text-slate-400" />
                    <span className="text-xs text-slate-400">Storage</span>
                  </div>
                  <p className="text-white font-semibold">
                    {organization?.limits?.max_storage_mb ? `${organization.limits.max_storage_mb} MB` : '0 MB'}
                  </p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Search className="w-4 h-4 text-slate-400" />
                    <span className="text-xs text-slate-400">Screenings/mes</span>
                  </div>
                  <p className="text-white font-semibold">
                    {organization?.limits?.max_screenings_per_month || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Trial Info Section */}
          {organization?.status === "trial" && organization?.trial_ends_at && (
            <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white">
                      Trial {({free:"Free",basic:"Basic",standard:"Standard",pro:"Pro",enterprise:"Enterprise"})[organization.plan] || "Pro"} activo
                    </h3>
                    <p className="text-slate-400 text-sm">
                      Tu trial termina el {new Date(organization.trial_ends_at).toLocaleDateString('es-MX', { 
                        day: 'numeric', month: 'long', year: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    try {
                      const response = await axios.post(
                        `${API}/billing/checkout`,
                        { plan: "standard", billing_period: "monthly", quantity: 3, coupon_code: null, origin_url: window.location.origin },
                        { withCredentials: true }
                      );
                      if (response.data.url) window.location.href = response.data.url;
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Error al iniciar checkout");
                    }
                  }}
                  className="bg-amber-600 hover:bg-amber-500 text-white"
                >
                  <Zap size={16} className="mr-2" />
                  Suscribirse ahora
                </Button>
              </div>
            </div>
          )}

          {/* Licenses & Billing (Owner only, paid plans only) */}
          {user?.role === "owner" && plan !== "free" && (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-white">Licencias y Facturación</h3>
                <Button
                  onClick={handleCustomerPortal}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                  size="sm"
                >
                  <FileText size={14} className="mr-2" />
                  Gestionar Facturación
                </Button>
              </div>

              {seatsInfo ? (
                <div className="space-y-4">
                  <div className="bg-slate-800/50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-slate-400">Licencias de Usuario</p>
                      <Button
                        onClick={() => setSeatsModalOpen(true)}
                        size="sm"
                        className="bg-teal-600 hover:bg-teal-500 text-white"
                      >
                        Ajustar Licencias
                      </Button>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-white">{seatsInfo.used_seats}</span>
                      <span className="text-slate-400">de</span>
                      <span className="text-2xl font-semibold text-white">{seatsInfo.total_seats}</span>
                      <span className="text-slate-400 text-sm">en uso</span>
                    </div>
                    {seatsInfo.available_seats > 0 && (
                      <p className="text-xs text-emerald-400 mt-1">
                        {seatsInfo.available_seats} {seatsInfo.available_seats === 1 ? 'licencia disponible' : 'licencias disponibles'}
                      </p>
                    )}
                    {seatsInfo.used_seats === seatsInfo.total_seats && (
                      <p className="text-xs text-amber-400 mt-1">
                        ⚠️ Todas las licencias están en uso. Aumenta las licencias para invitar más usuarios.
                      </p>
                    )}
                  </div>

                  <div className="text-xs text-slate-500">
                    <p>• Al invitar un usuario, se agregará automáticamente una licencia</p>
                    <p>• Al eliminar un usuario, la licencia permanece activa hasta que la reduces manualmente</p>
                    <p>• Mínimo de {seatsInfo.min_seats} {seatsInfo.min_seats === 1 ? 'licencia' : 'licencias'} para el plan {planName}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 text-slate-400 animate-spin mr-2" />
                  <span className="text-slate-400 text-sm">Cargando información de licencias...</span>
                </div>
              )}
            </div>
          )}

          {/* Logo Section */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <h3 className="text-base font-semibold text-white mb-4">Logo de la Empresa</h3>
            <div className="flex items-start gap-6">
              {/* Logo Preview */}
              <div className="flex-shrink-0">
                {settings.logo_url ? (
                  <div className="relative group">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}?t=${Date.now()}`}
                      alt="Logo"
                      className="w-32 h-32 object-contain bg-white rounded-lg border-2 border-slate-700"
                    />
                    <Button
                      onClick={handleDeleteLogo}
                      variant="outline"
                      size="sm"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-red-500/90 hover:bg-red-600 text-white border-none"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ) : (
                  <div className="w-32 h-32 bg-slate-800 rounded-lg border-2 border-dashed border-slate-700 flex items-center justify-center">
                    <Building2 size={40} className="text-slate-600" />
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div className="flex-1">
                <p className="text-sm text-slate-400 mb-3">
                  Suba el logo de su empresa que aparecerá en los documentos KYC generados.
                </p>
                <p className="text-xs text-slate-500 mb-4">
                  Formato: PNG, JPG o SVG • Tamaño máximo: 5MB
                </p>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={handleLogoUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <Button
                  type="button"
                  disabled={uploading}
                  className="bg-teal-600 hover:bg-teal-500 text-white cursor-pointer"
                  onClick={() => document.getElementById('logo-upload').click()}
                >
                  {uploading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload size={16} className="mr-2" />
                      {settings.logo_url ? "Cambiar Logo" : "Subir Logo"}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Company Info */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <h3 className="text-base font-semibold text-white mb-4">Información de la Empresa</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-slate-400 text-sm mb-2">Nombre de la Empresa *</Label>
                <Input
                  value={settings.company_name}
                  onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  placeholder="Ej: Notaría Pública 120"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>

              <div>
                <Label className="text-slate-400 text-sm mb-2">RFC / Tax ID</Label>
                <Input
                  value={settings.tax_id}
                  onChange={(e) => setSettings({ ...settings, tax_id: e.target.value })}
                  placeholder="Ej: ABC123456DEF"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>

              <div>
                <Label className="text-slate-400 text-sm mb-2">Teléfono</Label>
                <Input
                  value={settings.phone}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  placeholder="Ej: +52 998 123 4567"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>

              <div>
                <Label className="text-slate-400 text-sm mb-2">Email</Label>
                <Input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  placeholder="Ej: contacto@empresa.com"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>

              <div>
                <Label className="text-slate-400 text-sm mb-2">Sitio Web</Label>
                <Input
                  value={settings.website}
                  onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                  placeholder="Ej: www.empresa.com"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>

              <div className="md:col-span-2">
                <Label className="text-slate-400 text-sm mb-2">Dirección</Label>
                <Input
                  value={settings.address}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  placeholder="Ej: Av. Principal 123, Col. Centro, Cancún, Q. Roo"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
          </div>

          {/* Legal Representative */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <h3 className="text-base font-semibold text-white mb-4">Representante Legal</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 text-sm mb-2">Nombre Completo</Label>
                <Input
                  value={settings.legal_representative_name}
                  onChange={(e) => setSettings({ ...settings, legal_representative_name: e.target.value })}
                  placeholder="Ej: Lic. Juan Carlos Pérez"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>

              <div>
                <Label className="text-slate-400 text-sm mb-2">Cargo / Título</Label>
                <Input
                  value={settings.legal_representative_title}
                  onChange={(e) => setSettings({ ...settings, legal_representative_title: e.target.value })}
                  placeholder="Ej: Director General, Notario Público"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
          </div>

          {/* Business Line / Giro de Negocio */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Shield size={20} className="text-amber-400" />
              <h3 className="text-base font-semibold text-white">Giro de Negocio (Sujeto Obligado)</h3>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Cada giro tiene actividades vulnerables y obligaciones de reporte distintas bajo la LFPIORPI.
            </p>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-400 text-sm mb-2">Categoría de Negocio *</Label>
                <Select 
                  value={settings.business_line} 
                  onValueChange={(v) => setSettings({ ...settings, business_line: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Seleccionar giro..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {businessLines.map((bl) => (
                      <SelectItem key={bl.id} value={bl.id} className="text-white hover:bg-slate-700">
                        {bl.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {settings.business_line && businessLines.find((b) => b.id === settings.business_line) && (
                <div className="bg-teal-500/5 border border-teal-500/20 rounded-lg p-3">
                  <p className="text-sm text-teal-400 font-medium">
                    {businessLines.find((b) => b.id === settings.business_line)?.name}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {businessLines.find((b) => b.id === settings.business_line)?.label}
                  </p>
                </div>
              )}

              <div>
                <Label className="text-slate-400 text-sm mb-2">Razón Social</Label>
                <Input
                  value={settings.legal_name}
                  onChange={(e) => setSettings({ ...settings, legal_name: e.target.value })}
                  placeholder="Ej: Notaría Pública Número 120, S.C."
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>

              <div>
                <Label className="text-slate-400 text-sm mb-2">Actividad Vulnerable</Label>
                <Input
                  value={settings.activity_type}
                  onChange={(e) => setSettings({ ...settings, activity_type: e.target.value })}
                  placeholder="Descripción de actividad"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>
          </div>

          {/* AML Portal Registration */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <FileText size={20} className="text-violet-400" />
              <h3 className="text-base font-semibold text-white">Registro en Portal de Prevención de Lavado de Dinero</h3>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Todo Sujeto Obligado debe estar registrado en el Portal de Prevención de Lavado de Dinero. Suba el comprobante de registro.
            </p>

            <div className="flex items-center gap-4 mb-4">
              {amlStatus?.status === "uploaded" ? (
                <div className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-3 flex-1">
                  <CheckCircle size={18} className="text-emerald-400" />
                  <div>
                    <p className="text-sm text-emerald-400 font-medium">Documento Subido</p>
                    <p className="text-xs text-slate-400">{amlStatus.filename}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3 flex-1">
                  <AlertTriangle size={18} className="text-amber-400" />
                  <div>
                    <p className="text-sm text-amber-400 font-medium">Pendiente</p>
                    <p className="text-xs text-slate-400">Se requiere comprobante de registro AML</p>
                  </div>
                </div>
              )}
            </div>

            {organization ? (
              <div>
                <input
                  id="aml-upload"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleAmlUpload}
                  disabled={uploadingAml}
                  className="hidden"
                />
                <Button
                  type="button"
                  disabled={uploadingAml}
                  onClick={() => document.getElementById('aml-upload').click()}
                  variant="outline"
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  {uploadingAml ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload size={16} className="mr-2 text-teal-400" />
                      Subir Comprobante de Registro AML
                    </>
                  )}
                </Button>
                <p className="text-xs text-slate-500 mt-2">PDF, PNG o JPG (max 10MB)</p>
              </div>
            ) : (
              <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                <p className="text-xs text-slate-400">Guarde la configuración primero para subir el comprobante de registro AML.</p>
              </div>
            )}
          </div>

          {/* Compliance Status Summary */}
          {organization && (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-white mb-3">Estado de Cumplimiento</h3>
              <div className="flex flex-wrap gap-3">
                <Badge
                  className={
                    settings.business_line
                      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                      : "bg-red-500/10 text-red-400 border-red-500/20"
                  }
                >
                  {settings.business_line ? "Giro Configurado" : "Giro Pendiente"}
                </Badge>
                <Badge
                  className={
                    amlStatus?.status === "uploaded"
                      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                      : "bg-amber-500/10 text-amber-400 border-amber-500/20"
                  }
                >
                  {amlStatus?.status === "uploaded" ? "Registro AML Completo" : "Registro AML Pendiente"}
                </Badge>
                <Badge
                  className={
                    settings.business_line && amlStatus?.status === "uploaded"
                      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                      : "bg-red-500/10 text-red-400 border-red-500/20"
                  }
                >
                  {settings.business_line && amlStatus?.status === "uploaded"
                    ? "Cumplimiento Completo"
                    : "Cumplimiento Incompleto"}
                </Badge>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex items-center justify-between pt-4 border-t border-slate-800">
            <p className="text-xs text-slate-500">
              * Los campos marcados son requeridos para configurar correctamente su organización
            </p>
            <Button
              onClick={handleSave}
              disabled={saving || !settings.company_name || !settings.business_line}
              className="bg-teal-600 hover:bg-teal-500 text-white"
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Guardar Configuración
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
